import React, { useEffect, useRef, useState } from 'react';
import { Memory, Close, Menu as MenuIcon, ExpandMore, Logout, KeyboardArrowDown } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// Presentation-only layout for the role dashboards (MQBIT theme). Navigation state,
// permission checks and view switching stay in the dashboard pages — this file only
// renders what it's handed.

const getInitials = (name?: string) =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const withIconSize = (icon: React.ReactNode, size: number) =>
  React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<any>, { sx: { fontSize: size } })
    : icon;

/* ── Brand ─────────────────────────────────────────────────── */

export const BrandMark: React.FC<{ subtitle?: string }> = ({ subtitle = 'hrms.mqbit.in' }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-mqbit-700 to-mqbit-500 flex items-center justify-center text-white shadow-lg shadow-mqbit-600/30 shrink-0">
      <Memory sx={{ fontSize: 22 }} />
    </div>
    <div className="min-w-0">
      <span className="block text-xl font-extrabold text-white tracking-wide leading-tight">
        MQBIT <span className="text-mqbit-400">HRMS</span>
      </span>
      <p className="m-0 text-[10px] text-mqbit-300 font-medium tracking-tight truncate">{subtitle}</p>
    </div>
  </div>
);

/* ── Sidebar navigation primitives ─────────────────────────── */

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}

export const NavItem: React.FC<NavItemProps> = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group w-full flex items-center gap-3 px-3.5 py-2.5 text-sm rounded-xl border-0 cursor-pointer text-left transition-all ${
      active
        ? 'bg-mqbit-700 text-white font-semibold shadow-md shadow-mqbit-900/50'
        : 'bg-transparent text-mqbit-200 font-medium hover:text-white hover:bg-mqbit-900/60'
    }`}
  >
    <span className={`flex w-6 justify-center shrink-0 ${active ? 'text-mqbit-200' : 'text-mqbit-400 group-hover:text-mqbit-200'}`}>
      {withIconSize(icon, 20)}
    </span>
    <span className="truncate">{label}</span>
  </button>
);

export const NavSubItem: React.FC<NavItemProps> = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group w-full flex items-center gap-2.5 pl-3 pr-3 py-2 text-[13px] rounded-lg border-0 cursor-pointer text-left transition-all ${
      active
        ? 'bg-mqbit-700 text-white font-semibold shadow-md shadow-mqbit-900/40'
        : 'bg-transparent text-mqbit-300 font-medium hover:text-white hover:bg-mqbit-900/60'
    }`}
  >
    <span className={`flex w-5 justify-center shrink-0 ${active ? 'text-mqbit-200' : 'text-mqbit-500 group-hover:text-mqbit-300'}`}>
      {withIconSize(icon, 17)}
    </span>
    <span className="truncate">{label}</span>
  </button>
);

interface NavGroupProps {
  icon: React.ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const NavGroup: React.FC<NavGroupProps> = ({ icon, label, open, onToggle, children }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`group w-full flex items-center gap-3 px-3.5 py-2.5 text-sm font-medium rounded-xl border-0 cursor-pointer text-left transition-all ${
        open ? 'bg-mqbit-900/40 text-white' : 'bg-transparent text-mqbit-200 hover:text-white hover:bg-mqbit-900/60'
      }`}
    >
      <span className="flex w-6 justify-center shrink-0 text-mqbit-400 group-hover:text-mqbit-200">
        {withIconSize(icon, 20)}
      </span>
      <span className="flex-1 truncate">{label}</span>
      <ExpandMore sx={{ fontSize: 18 }} className={`text-mqbit-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="mt-1 mb-1.5 ml-[26px] pl-3 border-0 border-l border-solid border-mqbit-800/70 space-y-0.5">
        {children}
      </div>
    )}
  </div>
);

export const NavDivider: React.FC = () => <div className="my-3 mx-2 h-px bg-mqbit-900/70" />;

export const NavSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="m-0 px-3.5 pt-1 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-mqbit-400/80">{children}</p>
);

/* ── Header user menu ──────────────────────────────────────── */

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 p-1.5 rounded-xl bg-transparent border-0 cursor-pointer hover:bg-slate-100 transition focus:outline-none"
      >
        <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-solid border-emerald-200">
          <span className="w-1.5 h-1.5 mr-1.5 bg-emerald-500 rounded-full animate-pulse" /> Online
        </span>
        <span className="w-9 h-9 rounded-lg bg-gradient-to-tr from-mqbit-700 to-mqbit-500 ring-2 ring-mqbit-200 flex items-center justify-center text-white text-xs font-bold">
          {getInitials(user?.fullName)}
        </span>
        <span className="hidden md:block text-left max-w-[180px]">
          <span className="block text-xs font-bold text-slate-800 truncate capitalize">{user?.fullName}</span>
          <span className="block text-[11px] text-slate-400 truncate">{user?.role}</span>
        </span>
        <KeyboardArrowDown sx={{ fontSize: 18 }} className="text-slate-400 hidden sm:inline" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-solid border-slate-100 py-2 z-50">
          <div className="px-4 py-2.5 border-0 border-b border-solid border-slate-100">
            <p className="m-0 text-xs font-bold text-slate-800 truncate capitalize">{user?.fullName}</p>
            <p className="m-0 text-[11px] text-slate-400 truncate">{user?.email}</p>
            <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-mqbit-100 text-mqbit-700">
              {user?.role}
            </span>
          </div>
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-rose-600 bg-transparent border-0 cursor-pointer text-left hover:bg-rose-50 transition"
          >
            <Logout sx={{ fontSize: 16 }} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Header ────────────────────────────────────────────────── */

export const TopHeader: React.FC<{ title?: string; subtitle?: string; onMenuClick?: () => void; compact?: boolean }> = ({
  title = 'MQBIT Technologies',
  subtitle = 'AI & Digital Solutions Hub',
  onMenuClick,
  compact,
}) => (
  <header
    className={`${compact ? 'h-16' : 'h-20'} bg-white border-0 border-b border-solid border-slate-200 flex items-center justify-between px-4 sm:px-8 z-20 shrink-0 shadow-sm`}
  >
    <div className="flex items-center gap-4 min-w-0">
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="lg:hidden flex text-slate-600 hover:text-mqbit-700 p-2 rounded-xl bg-slate-100 border-0 cursor-pointer"
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </button>
      )}
      <div className="min-w-0">
        <h2 className="m-0 text-base font-bold text-slate-800 truncate">{title}</h2>
        <p className="m-0 text-xs text-slate-500 hidden sm:block truncate">{subtitle}</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <UserMenu />
    </div>
  </header>
);

/* ── Shell ─────────────────────────────────────────────────── */

interface AppShellProps {
  panelTitle: string;
  nav: React.ReactNode;
  mobileOpen?: boolean;
  onDrawerClose?: () => void;
  onDrawerToggle?: () => void;
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ panelTitle, nav, mobileOpen = false, onDrawerClose, onDrawerToggle, children }) => {
  const { user } = useAuth();

  return (
    <div className="h-screen flex bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      <div
        onClick={onDrawerClose}
        className={`fixed inset-0 z-30 bg-slate-950/60 backdrop-blur-sm lg:hidden ${mobileOpen ? 'block' : 'hidden'}`}
      />

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-[272px] bg-mqbit-950 text-slate-300 flex flex-col shrink-0 border-0 border-r border-solid border-mqbit-900/50 shadow-xl transition-transform duration-300 lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-20 flex items-center justify-between px-6 bg-mqbit-950/80 border-0 border-b border-solid border-mqbit-900/60 shrink-0">
          <BrandMark />
          <button
            type="button"
            onClick={onDrawerClose}
            aria-label="Close navigation menu"
            className="lg:hidden flex bg-transparent border-0 cursor-pointer text-mqbit-300 hover:text-white"
          >
            <Close sx={{ fontSize: 20 }} />
          </button>
        </div>

        <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-5 space-y-1">
          <NavSectionLabel>{panelTitle}</NavSectionLabel>
          {nav}
        </nav>

        <div className="p-4 border-0 border-t border-solid border-mqbit-900/60 bg-mqbit-950/50 shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-tr from-mqbit-700 to-mqbit-500 ring-2 ring-mqbit-500/50 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {getInitials(user?.fullName)}
            </span>
            <div className="overflow-hidden">
              <p className="m-0 text-sm font-bold text-white truncate capitalize">{user?.fullName}</p>
              <p className="m-0 text-xs text-mqbit-300 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopHeader onMenuClick={onDrawerToggle} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 bg-slate-50">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
